/** The acceptance gate's state: one item per outstanding policy, moved along by what the person does. */
import type { Requirement } from "./legal";

export type ItemStatus = "unread" | "read" | "accepting" | "accepted" | "failed";

export interface GateItem {
  requirement: Requirement;
  status: ItemStatus;
  error: string | null;
  /** A newer version replaced the one being read: it must be opened and read again. */
  superseded: boolean;
}

export interface GateState {
  items: GateItem[];
}

export type GateAction =
  | { type: "load"; requirements: Requirement[] }
  | { type: "opened"; versionId: string }
  | { type: "accepting"; versionId: string }
  | { type: "accepted"; versionId: string }
  | { type: "failed"; versionId: string; message: string }
  | { type: "superseded"; documentId: string };

export const initialGate: GateState = { items: [] };

function patch(
  state: GateState,
  versionId: string,
  from: ItemStatus[],
  change: Partial<GateItem>,
): GateState {
  return {
    items: state.items.map((i) =>
      i.requirement.version_id === versionId && from.includes(i.status) ? { ...i, ...change } : i,
    ),
  };
}

export function gateReducer(state: GateState, action: GateAction): GateState {
  switch (action.type) {
    case "load": {
      const before = new Map(state.items.map((i) => [i.requirement.version_id, i]));
      const supersededDocs = new Set(
        state.items.filter((i) => i.superseded).map((i) => i.requirement.document_id),
      );
      return {
        items: action.requirements.map((requirement) => {
          const kept = before.get(requirement.version_id);
          return {
            requirement,
            status: kept?.status ?? "unread",
            error: kept?.error ?? null,
            superseded: supersededDocs.has(requirement.document_id),
          };
        }),
      };
    }
    case "opened":
      return patch(state, action.versionId, ["unread"], { status: "read" });
    case "accepting":
      return patch(state, action.versionId, ["read", "failed"], { status: "accepting", error: null });
    case "accepted":
      return patch(state, action.versionId, ["accepting"], {
        status: "accepted",
        error: null,
        superseded: false,
      });
    case "failed":
      return patch(state, action.versionId, ["accepting"], { status: "failed", error: action.message });
    case "superseded":
      return {
        items: state.items.map((i) =>
          i.requirement.document_id === action.documentId && i.status !== "accepted"
            ? { ...i, status: "unread", error: null, superseded: true }
            : i,
        ),
      };
  }
}

export const canAccept = (item: GateItem): boolean => item.status === "read" || item.status === "failed";

export const canContinue = (state: GateState): boolean =>
  state.items.length > 0 && state.items.every((i) => i.status === "accepted");
